import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import dayjs from 'dayjs';

import { heySynclyService } from '@/entities/hey-syncly';
import { ApiError, type ApiErrorData } from '@/error-handler/ApiError';
import accountService from '@/features/Account/api';
import { reportService } from '@/features/Report/api';
import { scrapingService } from '@/features/Scraping/api';
import { studioService } from '@/features/Studio/api/service';
import { wordCloudService } from '@/features/WordCloud/api';

// 재시도 횟수를 추적하기 위한 타입 확장
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

// 최대 재시도 횟수
const MAX_RETRY_COUNT = 1;
const AUTH_ERROR_STATUS = 418;

// 토큰 갱신 상태 관리
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
  config: RetryableRequestConfig;
}> = [];

// 대기 중인 요청들 처리
const processQueue = (error: unknown = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      resolve(config);
    }
  });
  failedQueue = [];
};
import agentService from './agent';
import analysisService from './analysis';
import appleAppService from './apple_app_store';
import attachmentService from './attachment';
import authService from './auth';
import chartService from './chart';
import contactService from './contact';
import customerService from './customer';
import dashboard2Service from './dashboard2';
import exclusionService from './exclusion';
import exportService from './export';
import feedbackService from './feedback';
import filterItemsService from './filter-items';
import globalSearchService from './global-search';
import googlePlayService from './google_play';
import gptService from './gpt';
import homeService from './home';
import insightExplorerService from './insight-explorer';
import integrationService from './integrations';
import atlasService from './integrations/atlas';
import channeltalkService from './integrations/channeltalk';
import discordService from './integrations/discord';
import FreshdeskService from './integrations/freshdesk';
import frontService from './integrations/front';
import g2Service from './integrations/g2';
import gongService from './integrations/gong';
import gorgiasService from './integrations/gorgias';
import helpscoutService from './integrations/helpscout';
import intercomService from './integrations/intercom';
import knocommerceService from './integrations/knocommerce';
import kustomerService from './integrations/kustomer';
import okendoService from './integrations/okendo';
import { richpanelService } from './integrations/richpanel';
import salesforceService from './integrations/salesforce';
import slackService from './integrations/slack';
import slackWebhookServices from './integrations/slack_webhook';
import stampedService from './integrations/stamped';
import tiktokshopService from './integrations/tiktokshop';
import trustpilotService from './integrations/trustpilot';
import typeformService from './integrations/typeform';
import zendeskService from './integrations/zendesk';
import savedTopicService from './saved-topic';
import tagService from './tag';
import taxonomyService from './taxonomy';
import utilService from './util';
import workflowService from './workflow';
import workspaceService from './workspace';

const PLAYWRIGHT_PROXY_PATH = '/__playwright_backend__/';

const isLocalPlaywrightRuntime = () =>
  typeof window !== 'undefined' &&
  typeof navigator !== 'undefined' &&
  navigator.webdriver &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname);

const getBackendBaseUrl = () =>
  isLocalPlaywrightRuntime()
    ? PLAYWRIGHT_PROXY_PATH
    : import.meta.env.VITE_PUBLIC_BACKEND_URL;

export const setDefaultTimeZone = (timezone: string) => {
  defaultAxios.defaults.headers.common['Timezone'] = timezone;
  v1ApiClient.defaults.headers.common['Timezone'] = timezone;
  dayjs.tz.setDefault(timezone);
};

export const defaultAxios = axios.create({
  baseURL: getBackendBaseUrl(),
  withCredentials: true,
});

export const v1ApiClient = axios.create({
  baseURL: `${getBackendBaseUrl()}v1/`,
  withCredentials: true,
});

export const lambdaAxios = axios.create({
  baseURL: import.meta.env.VITE_PUBLIC_LAMBDA_URL,
});

export const mockupReq = {
  get: <T>(name: string, data: T, delay?: number): Promise<T> => {
    const res = JSON.parse(localStorage.getItem(name) as string) || data;
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(res);
      }, delay || 300);
    });
  },
  post: <T>(name: string, data: T, delay?: number): Promise<T> => {
    localStorage.setItem(name, JSON.stringify(data));
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(data);
      }, delay || 300);
    });
  },
};

// API 에러를 ApiError로 변환
const convertToApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError<ApiErrorData>(error)) {
    const apiError = error.response?.data;
    if (apiError) {
      return new ApiError(apiError);
    }
  }
  return new ApiError({
    code: (error as AxiosError).status || 500,
    data: {
      client_code: 'unknown',
      data: undefined,
    },
    message: 'Unknown error',
    stack: (error as AxiosError).stack || '',
  });
};

// 인증 에러(418) 처리 및 토큰 갱신 인터셉터
// 임시방편: 레거시 백엔드에서 401 의미가 혼재되어 있어 실제 auth(token) 에러만 418로 처리
// 관련 티켓: https://linear.app/syncly/issue/SYN-6474/401-error-핸들링-방식-변경
const handleAuthError = async (
  error: AxiosError,
  axiosInstance: typeof defaultAxios,
) => {
  const originalRequest = error.config as RetryableRequestConfig | undefined;

  // 인증 에러(418)가 아니거나 설정이 없으면 에러 그대로 throw
  if (error.response?.status !== AUTH_ERROR_STATUS || !originalRequest) {
    throw convertToApiError(error);
  }

  // /auth/refresh 요청 자체가 실패한 경우 로그인 페이지로 이동
  if (originalRequest.url?.includes('/auth/refresh')) {
    isRefreshing = false;
    processQueue(error);
    window.location.href = '/login';
    throw convertToApiError(error);
  }

  // 재시도 횟수 초과 시 로그인 페이지로 이동 (무한 루프 방지)
  const retryCount = originalRequest._retryCount ?? 0;
  if (retryCount >= MAX_RETRY_COUNT) {
    isRefreshing = false;
    processQueue(error);
    window.location.href = '/login';
    throw convertToApiError(error);
  }

  // 이미 토큰 갱신 중이면 대기열에 추가
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject, config: originalRequest });
    }).then((config) => {
      const retryConfig = config as RetryableRequestConfig;
      retryConfig._retryCount = (retryConfig._retryCount ?? 0) + 1;
      return axiosInstance(retryConfig);
    });
  }

  isRefreshing = true;

  try {
    // 토큰 갱신 시도
    await defaultAxios.get('/auth/refresh');

    // 갱신 성공 - 대기 중인 요청들 재시도
    processQueue();
    isRefreshing = false;

    // 원래 요청 재시도 (재시도 횟수 증가)
    originalRequest._retryCount = retryCount + 1;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    // 갱신 실패 - 로그인 페이지로 이동
    processQueue(refreshError);
    isRefreshing = false;
    window.location.href = '/login';
    throw convertToApiError(refreshError);
  }
};

// 인터셉터 등록
defaultAxios.interceptors.response.use(
  (res) => res,
  (error) => handleAuthError(error, defaultAxios),
);
v1ApiClient.interceptors.response.use(
  (res) => res,
  (error) => handleAuthError(error, v1ApiClient),
);

export const fetcher = (url: string) =>
  defaultAxios.get(url).then((res) => res.data);

export const didAbort = (error: unknown) => {
  return axios.isCancel(error);
};

const service = {
  auth: authService,
  workspace: workspaceService,
  util: utilService,

  // features
  feedback: feedbackService,
  attachment: attachmentService,
  tag: tagService,
  account: accountService,
  contact: contactService,
  agent: agentService,
  globalSearch: globalSearchService,
  exclusion: exclusionService,
  chart: chartService,
  dashboard2: dashboard2Service,
  workflow: workflowService,
  gpt: gptService,
  home: homeService,
  customer: customerService,
  filterItems: filterItemsService,
  export: exportService,
  taxonomy: taxonomyService,
  savedTopic: savedTopicService,
  insightExplorer: insightExplorerService,
  analysis: analysisService,
  report: reportService,
  wordCloud: wordCloudService,

  // integration
  integration: integrationService,

  // integration services
  gorgias: gorgiasService,
  slack: slackService,
  channeltalk: channeltalkService,
  salesforce: salesforceService,
  intercom: intercomService,
  slackWebhook: slackWebhookServices,
  front: frontService,
  zendesk: zendeskService,
  discord: discordService,
  atlas: atlasService,
  typeform: typeformService,
  googlePlay: googlePlayService,
  appleAppStore: appleAppService,
  g2: g2Service,
  kustomer: kustomerService,
  gong: gongService,
  freshdesk: FreshdeskService,
  stamped: stampedService,
  okendo: okendoService,
  trustpilot: trustpilotService,
  tiktokshop: tiktokshopService,
  knocommerce: knocommerceService,
  helpscout: helpscoutService,
  richpanel: richpanelService,
  scraping: scrapingService,
  heySyncly: heySynclyService,
  studio: studioService,
};

export default service;
